import { FolderTree, Plus } from 'lucide-react'
import * as React from 'react'
import { useCategoryApi, Category_ } from 'src/api/db/apiCategory'
import { Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Input } from 'src/shared/components/ui/input'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { PageContainer } from 'src/shared/components/PageContainer'
import { AddEditCategoryDialog } from './components/AddEditCategoryDialog'
import { CategoryTree } from './components/CategoryTree'
import { MoveProductsDialog, MoveMode } from './components/MoveProductsDialog'
import { useSupabaseClient } from 'src/api/supabase'
import { DeleteCategoryDialog } from './components/DeleteCategoryDialog'

export const CategoriesPage = () => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const supabase = useSupabaseClient()
  const {
    allCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    bulkMoveProducts,
  } = useCategoryApi({})
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [isMoveDialogOpen, setIsMoveDialogOpen] = React.useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [editingCategory, setEditingCategory] =
    React.useState<Category_ | null>(null)
  const [deletingCategory, setDeletingCategory] =
    React.useState<Category_ | null>(null)
  const [movingCategory, setMovingCategory] = React.useState<Category_ | null>(
    null
  )
  const [movingProductCount, setMovingProductCount] = React.useState(0)
  const [targetCategoryId, setTargetCategoryId] = React.useState<number | null>(
    null
  )

  const categories = React.useMemo(
    () => allCategories?.data || [],
    [allCategories?.data]
  )

  // Get translated category name
  const getCategoryName = React.useCallback(
    (category: Category_) => {
      const parts = category.title?.split(' || ') || []
      return t(
        parts[0] || 'Category',
        parts[1] || category.title || `Category ${category.id}`
      )
    },
    [t]
  )

  const handleEdit = (category: Category_) => {
    setEditingCategory(category)
    setIsDialogOpen(true)
  }

  const handleDelete = (id: number) => {
    const category = categories.find((cat) => cat.id === id)
    if (category) {
      setDeletingCategory(category)
      setIsDeleteDialogOpen(true)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deletingCategory) return

    try {
      await deleteCategory.mutateAsync(deletingCategory.id)
      toast({
        variant: 'success',
        title: t('Success', 'Lyckades!'),
        description: t('Category deleted successfully', 'Kategori raderad'),
      })
      setIsDeleteDialogOpen(false)
      setDeletingCategory(null)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('Error', 'Fel'),
        description: t(
          'Failed to delete category',
          'Kunde inte radera kategori'
        ),
      })
    }
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    setEditingCategory(null)
  }

  const handleSave = async (formData: {
    title: string
    description?: string
    color?: string
    image_url?: string
    parent_category_id?: number | null
  }) => {
    try {
      if (editingCategory) {
        await updateCategory.mutateAsync({
          id: editingCategory.id,
          updates: formData,
        })
        toast({
          variant: 'success',
          title: t('Success', 'Lyckades!'),
          description: t(
            'Category updated successfully',
            'Kategori uppdaterad'
          ),
        })
      } else {
        await createCategory.mutateAsync(formData)
        toast({
          variant: 'success',
          title: t('Success', 'Lyckades!'),
          description: t('Category created successfully', 'Kategori skapad'),
        })
      }
      handleDialogClose()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('Error', 'Fel'),
        description: editingCategory
          ? t('Failed to update category', 'Kunde inte uppdatera kategori')
          : t('Failed to create category', 'Kunde inte skapa kategori'),
      })
    }
  }

  const handleMoveProducts = (category: Category_, productCount: number) => {
    setMovingCategory(category)
    setMovingProductCount(productCount)
    setIsMoveDialogOpen(true)
  }

  const handleCategoryDrop = async (
    sourceCategoryId: number,
    targetCategory: Category_
  ) => {
    // Find the source category
    const sourceCategory = categories.find((cat) => cat.id === sourceCategoryId)
    if (!sourceCategory) return

    // Get product count for source category
    const { count, error } = await supabase
      .from('category_product')
      .select('*', { count: 'exact', head: true })
      .eq('category_id', sourceCategoryId)

    if (error) {
      toast({
        variant: 'destructive',
        title: t('Error', 'Fel'),
        description: t(
          'Failed to get product count',
          'Kunde inte hämta produktantal'
        ),
      })
      return
    }

    const productCount = count || 0

    // Set the moving category, target, and product count, then open the dialog
    setMovingCategory(sourceCategory)
    setMovingProductCount(productCount)
    setTargetCategoryId(targetCategory.id)
    setIsMoveDialogOpen(true)
  }

  const handleConfirmMove = async (
    targetCategoryId: number,
    mode: MoveMode
  ) => {
    if (!movingCategory) return

    try {
      if (mode === 'category') {
        // Move the category itself by updating its parent_category_id
        await updateCategory.mutateAsync({
          id: movingCategory.id,
          updates: { parent_category_id: targetCategoryId },
        })

        toast({
          variant: 'success',
          title: t('Success', 'Lyckades!'),
          description: t('Category moved successfully', 'Kategori flyttad'),
        })
      } else {
        // Move only products
        // Get all product IDs in the source category
        const { data: categoryProducts, error: fetchError } = await supabase
          .from('category_product')
          .select('product_id')
          .eq('category_id', movingCategory.id)

        if (fetchError) throw fetchError

        const productIds = (categoryProducts || [])
          .map((cp) => cp.product_id)
          .filter((id): id is number => id !== null)

        if (productIds.length === 0) {
          toast({
            variant: 'destructive',
            title: t('Error', 'Fel'),
            description: t(
              'No products found in category',
              'Inga produkter hittades i kategorin'
            ),
          })
          return
        }

        // Move all products
        const result = await bulkMoveProducts.mutateAsync({
          fromCategoryId: movingCategory.id,
          toCategoryId: targetCategoryId,
          productIds,
        })

        toast({
          variant: 'success',
          title: t('Success', 'Lyckades!'),
          description: t(
            `Successfully moved ${result.updated} product(s)`,
            `Flyttade ${result.updated} produkt(er)`
          ),
        })
      }

      setIsMoveDialogOpen(false)
      setMovingCategory(null)
      setMovingProductCount(0)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('Error', 'Fel'),
        description:
          mode === 'category'
            ? t('Failed to move category', 'Kunde inte flytta kategori')
            : t('Failed to move products', 'Kunde inte flytta produkter'),
      })
    }
  }

  return (
    <>
      <PageContainer width="xl">
        <Row className="gap-2 items-center mb-4 py-2">
          <FolderTree className="h-6 w-6" />
          <h1 className="text-2xl">{t('Categories', 'Kategorier')}</h1>

          <Input
            placeholder={t('Search categories...', 'Sök kategorier...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm mx-4"
          />

          <Button
            variant="primary"
            className="ml-auto"
            onClick={() => {
              setEditingCategory(null)
              setIsDialogOpen(true)
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('Add Category', 'Lägg till kategori')}
          </Button>
        </Row>

        <div
          className="border rounded-lg bg-white"
          style={{
            height: 'calc(100vh - 190px)',
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          <CategoryTree
            categories={categories}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onMoveProducts={handleMoveProducts}
            onCategoryDrop={handleCategoryDrop}
            searchQuery={searchQuery}
          />
        </div>

        <div className="flex items-center justify-end space-x-2 py-4">
          <div className="flex-1 text-sm text-muted-foreground">
            {categories.length} {t('category(ies)', 'kategori(er)')}
          </div>
        </div>
      </PageContainer>

      <AddEditCategoryDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        category={editingCategory}
        allCategories={categories}
      />

      <MoveProductsDialog
        isOpen={isMoveDialogOpen}
        onClose={() => {
          setIsMoveDialogOpen(false)
          setMovingCategory(null)
          setMovingProductCount(0)
          setTargetCategoryId(null)
        }}
        onConfirm={handleConfirmMove}
        sourceCategory={movingCategory}
        productCount={movingProductCount}
        isLoading={bulkMoveProducts.isPending || updateCategory.isPending}
        allCategories={categories}
        targetCategoryId={targetCategoryId}
      />

      {deletingCategory && (
        <DeleteCategoryDialog
          open={isDeleteDialogOpen}
          categoryName={getCategoryName(deletingCategory)}
          onOpenChange={(open) => {
            setIsDeleteDialogOpen(open)
            if (!open) {
              setDeletingCategory(null)
            }
          }}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  )
}
